import * as THREE from "three"
import { pickHandleAtPointer } from "../helpers/hover-tooltip.helper"

/**
 * Controller del tooltip de hover sobre los carriles de avance / hitboxes del
 * overlay interactivo (engine-adjacent, mismo patrón que AdvanceOverlay /
 * HighlightOverlay).
 *
 * POSEE el raycaster, el Vector2 del pointer y el binding de eventos del canvas
 * — todo fuera de la reactividad de Vue (ver feedback vue2 + three: no meter
 * refs ni estructuras THREE en data()). El host le inyecta por callbacks lo que
 * es suyo: qué grupos intersectar (getTargets), la resolución de negocio para un
 * handle (resolve) y el render del tooltip (onShow / onHide).
 */
export class HoverTooltipController {
  /**
   * @param {object} cfg
   * @param {() => any}                        cfg.getViewer  devuelve el CadViewer (puente) o null.
   * @param {() => object[]}                   cfg.getTargets grupos THREE a intersectar.
   * @param {(handle:string, entity:object) => object|null} cfg.resolve negocio → { totalLength, totalMeters, breakdown } (o null para ocultar).
   * @param {(tip:object) => void}             cfg.onShow     pinta el tooltip { x, y, totalLength, totalMeters, breakdown }.
   * @param {() => void}                       cfg.onHide     oculta el tooltip.
   */
  constructor({ getViewer, getTargets, resolve, onShow, onHide }) {
    this._getViewer  = getViewer
    this._getTargets = getTargets
    this._resolve    = resolve
    this._onShow     = onShow
    this._onHide     = onHide

    this._raycaster    = null
    this._pointer      = null
    this._canvas       = null
    this._dxf          = null
    this._moveHandler  = null
    this._leaveHandler = null
  }

  /** Engancha pointerdown/move/leave al canvas del visor (idempotente). */
  bind() {
    if (this._moveHandler) return
    const dxf = this._getViewer?.()?.GetDxfViewer?.()
    const canvas = dxf?.GetCanvas?.()
    if (!canvas) return

    this._dxf       = dxf
    this._canvas    = canvas
    this._raycaster = new THREE.Raycaster()
    this._pointer   = new THREE.Vector2()
    this._pressed   = false

    // CAPTURA (useCapture=true): OrbitControls maneja el pan sobre el MISMO
    // canvas y puede parar la propagación de los pointer events; registrándonos
    // en captura nos ejecutamos ANTES que él, así el tooltip se oculta aunque el
    // control se "coma" el evento en fase bubble.
    this._downHandler  = () => { this._pressed = true; this.hide() }
    this._upHandler    = () => { this._pressed = false }
    this._moveHandler  = (event) => this._OnPointerMove(event)
    this._leaveHandler = () => this.hide()
    canvas.addEventListener('pointerdown', this._downHandler, true)
    window.addEventListener('pointerup', this._upHandler, true)
    canvas.addEventListener('pointermove', this._moveHandler, true)
    canvas.addEventListener('pointerleave', this._leaveHandler, true)
  }

  /** Desengancha los listeners, libera recursos y oculta el tooltip. */
  unbind() {
    if (this._canvas && this._moveHandler) {
      try {
        this._canvas.removeEventListener('pointerdown', this._downHandler, true)
        window.removeEventListener('pointerup', this._upHandler, true)
        this._canvas.removeEventListener('pointermove', this._moveHandler, true)
        this._canvas.removeEventListener('pointerleave', this._leaveHandler, true)
      } catch (e) { /* canvas ya removido */ }
    }
    this._canvas       = null
    this._dxf          = null
    this._downHandler  = null
    this._upHandler    = null
    this._moveHandler  = null
    this._leaveHandler = null
    this._raycaster    = null
    this._pointer      = null
    this._pressed      = false
    this.hide()
  }

  hide() {
    this._onHide?.()
  }

  _OnPointerMove(event) {
    // Si hay un botón pulsado (flag propio o event.buttons), el usuario está
    // arrastrando (pan): ocultamos el tooltip y no hacemos hit-test.
    if (this._pressed || event.buttons !== 0) {
      this.hide()
      return
    }

    const camera = this._dxf?.GetCamera?.()
    if (!camera) return

    const targets = this._getTargets?.() || []
    const picked = pickHandleAtPointer(this._raycaster, this._pointer, camera, event, this._canvas, targets)
    if (!picked) {
      this.hide()
      return
    }

    const tip = this._resolve?.(picked.handle, picked.entity)
    if (!tip) {
      this.hide()
      return
    }

    // Coords crudas del puntero: el widget ancla su cola a este punto y se
    // coloca debajo (el offset/anclaje es presentación, vive en el widget).
    this._onShow?.({
      x:           event.clientX,
      y:           event.clientY,
      totalLength: tip.totalLength,
      totalMeters: tip.totalMeters,
      breakdown:   tip.breakdown,
    })
  }
}
